import { createPlatePlugin } from 'platejs/react'
import type { AnyPlatePlugin } from 'platejs/react'

import ComposerActionCreators from '~publish/legacy/composer/composer/action-creators/ComposerActionCreators'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import type { BufferEditor } from '../BufferEditor/types.plate'

export const THREADS_PLUGIN_KEY = 'threads-plugin'

function calculateAbsoluteOffset(editor: BufferEditor): number {
  const [pathX, pathY] = editor.selection?.focus.path || [0, 0]
  // get current position within current node
  let offset = editor.selection?.focus.offset || 0

  for (const node of editor.api.nodes({
    at: [],
    voids: false,
    match: (entry, path) => {
      if (
        path.length >= 2 &&
        // get all nodes that are before the current selection
        (path[0] < pathX || (path[0] === pathX && path[1] < pathY))
      ) {
        return !!entry.text
      }
      return false
    },
  })) {
    if (typeof node[0].text === 'string') {
      offset += node[0].text.length
    }
  }

  // add all new lines before the current selection
  return offset + pathX
}

// Not returned directly from the arrow function: an explicit return type on
// the expression itself back-propagates into createPlatePlugin's own
// generic inference and corrupts it (options/node end up typed against the
// annotation's defaults instead of the literal passed in).
export const createNewThreadPostPlugin = (): AnyPlatePlugin => {
  const plugin = createPlatePlugin({
    key: THREADS_PLUGIN_KEY,
    handlers: {
      onKeyDown: ({ editor: rawEditor, event }): void | boolean => {
        const editor = rawEditor as unknown as BufferEditor
        if (event.key === 'Backspace') {
          const currentThreadId = ComposerStore.getActiveThreadId()
          const numberOfThreads =
            ComposerStore.getNumberOfThreads(editor.id) || 0

          if (
            editor.selection?.anchor &&
            editor.api.isStart(editor.selection?.anchor, []) &&
            numberOfThreads > 1
          ) {
            ComposerActionCreators.deleteThreadedDraft(
              editor.id,
              currentThreadId,
              true,
            )

            event.preventDefault()

            return true
          }
        } else if (event.key === 'Enter' && event.shiftKey) {
          ComposerActionCreators.updateDraftAddPostToThread(
            editor.id,
            calculateAbsoluteOffset(editor),
          )

          event.preventDefault()

          return true
        } else if (event.key === 'ArrowUp') {
          if (
            editor.selection?.anchor &&
            editor.api.isStart(editor.selection?.anchor, [])
          ) {
            const currentThreadId = ComposerStore.getActiveThreadId()

            if (currentThreadId > 0) {
              ComposerActionCreators.switchActiveThreadEditor(
                editor.id,
                currentThreadId - 1,
              )
              event.preventDefault()
              return true
            }
          }
        } else if (event.key === 'ArrowDown') {
          if (
            editor.selection?.anchor &&
            editor.api.isEnd(editor.selection?.anchor, [])
          ) {
            const numberOfThreads =
              ComposerStore.getNumberOfThreads(editor.id) || 0
            const nextActiveThreadId = ComposerStore.getActiveThreadId() + 1

            if (nextActiveThreadId < numberOfThreads) {
              ComposerActionCreators.switchActiveThreadEditor(
                editor.id,
                nextActiveThreadId,
              )
              event.preventDefault()
              return true
            }
          }
        }
      },
    },
  })
  return plugin
}
